import {
  FilterDocumentDateFilter,
  FilterDocumentDateFilterTypes,
  ID,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  dateFilterPresetGroups,
  goalActiveFilterPresetGroups,
  inferCustomRangeType,
} from '@griply/utils';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Info16x16 } from 'shared/assets/icons';
import { Option } from 'shared/components/ui/custom-form-select';
import { DateButton } from 'shared/components/ui/date-button';
import { DatepickerWithOptions } from 'shared/components/ui/datepicker-with-options';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuSelect,
} from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { ActiveFilter } from 'shared/types/filter';
import { formatDeadlineFilterDisplay } from 'shared/utils/filters/format-deadline-filter-display';

import * as Styled from './filter-date-range.style';

// const clearButtonId = 'clear';
enum MenusToDisplay {
  Custom = 'custom',
  DatePicker = 'datepicker',
}
enum DatePickerOptions {
  Start = 'start',
  End = 'end',
}

const futurePresets = new Set([
  FilterDocumentDateFilterTypes.ThisWeek,
  FilterDocumentDateFilterTypes.NextWeek,
  FilterDocumentDateFilterTypes.ThisMonth,
  FilterDocumentDateFilterTypes.NextMonth,
  FilterDocumentDateFilterTypes.ThisQuarter,
  FilterDocumentDateFilterTypes.NextQuarter,
  FilterDocumentDateFilterTypes.ThisYear,
  FilterDocumentDateFilterTypes.NextYear,
  FilterDocumentDateFilterTypes.Overdue,
  FilterDocumentDateFilterTypes.Next14Days,
]);

export interface FilterDateRangeProps {
  id: ID;
  label: string;
  activeOptions: ActiveFilter[];
  onChange: (value: FilterDocumentDateFilter | null) => void;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  level?: number;
  compact?: boolean;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  canClear?: boolean;
  isDeadline?: boolean; // when more date options than just `deadline` and `date` should be supported, do this differently
  hideUndefinedDateOptions?: boolean;
  hideFuturePresets?: boolean;
  infoUrl?: string;
}

export const FilterDateRange: React.FC<FilterDateRangeProps> = ({
  id,
  label,
  activeOptions,
  onChange,
  dateFormat,
  weekStartsOn,
  level,
  compact,
  icon,
  canClear,
  isDeadline,
  hideUndefinedDateOptions,
  hideFuturePresets,
  infoUrl,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const [openMenu, setOpenMenu] = useState<MenusToDisplay>();
  const containerRef = useRef<HTMLLabelElement>(null);

  const activeFilter = activeOptions.find((active) => active.id === id);
  const value = activeFilter?.dateFilter;

  const [localStartDate, setLocalStartDate] = useState<Date | null>(
    value?.custom?.start ?? null,
  );
  const [localEndDate, setLocalEndDate] = useState<Date | null>(
    value?.custom?.end ?? null,
  );
  const [datePickerOption, setDatePickerOption] = useState<DatePickerOptions>(
    DatePickerOptions.Start,
  );

  const openStartDatePicker = () => {
    setOpenMenu(MenusToDisplay.DatePicker);
    setDatePickerOption(DatePickerOptions.Start);
  };

  const openEndDatePicker = () => {
    setOpenMenu(MenusToDisplay.DatePicker);
    setDatePickerOption(DatePickerOptions.End);
  };

  const cancelDatePicker = () => {
    setOpenMenu(MenusToDisplay.Custom);
  };

  const onSetCustomDate = (date: Timestamp | undefined) => {
    setOpenMenu(MenusToDisplay.Custom);
    datePickerOption === DatePickerOptions.Start
      ? setLocalStartDate(date ?? null)
      : setLocalEndDate(date ?? null);
  };

  const handlePresetSelect = (_: string, preset?: string) => {
    if (!preset) {
      onChange(null);
      return;
    }

    if (preset === FilterDocumentDateFilterTypes.Custom) {
      setOpenMenu(MenusToDisplay.Custom);
      return;
    }

    onChange({
      type: preset as FilterDocumentDateFilterTypes,
      custom: null,
    });
  };

  const handleCustomDateChange = () => {
    const customType = inferCustomRangeType(localStartDate, localEndDate);
    const hasDates = !!localStartDate || !!localEndDate;

    onChange(
      hasDates
        ? {
            type: FilterDocumentDateFilterTypes.Custom,
            custom: {
              type: customType,
              start: localStartDate,
              end: localEndDate,
            },
          }
        : null,
    );

    setOpenMenu(undefined);
  };

  const presetOptions = useMemo(() => {
    const labelKeys: Record<FilterDocumentDateFilterTypes, string> = {
      [FilterDocumentDateFilterTypes.Overdue]:
        'context-menus.filtering.task.filters.date.presets.overdue',
      [FilterDocumentDateFilterTypes.Today]:
        'context-menus.filtering.task.filters.date.presets.today',
      [FilterDocumentDateFilterTypes.ThisWeek]:
        'context-menus.filtering.task.filters.date.presets.this-week',
      [FilterDocumentDateFilterTypes.NextWeek]:
        'context-menus.filtering.task.filters.date.presets.next-week',
      [FilterDocumentDateFilterTypes.Next14Days]:
        'context-menus.filtering.task.filters.date.presets.next-14-days',
      [FilterDocumentDateFilterTypes.ThisMonth]:
        'context-menus.filtering.task.filters.date.presets.this-month',
      [FilterDocumentDateFilterTypes.NextMonth]:
        'context-menus.filtering.task.filters.date.presets.next-month',
      [FilterDocumentDateFilterTypes.ThisQuarter]:
        'context-menus.filtering.task.filters.date.presets.this-quarter',
      [FilterDocumentDateFilterTypes.NextQuarter]:
        'context-menus.filtering.task.filters.date.presets.next-quarter',
      [FilterDocumentDateFilterTypes.ThisYear]:
        'context-menus.filtering.task.filters.date.presets.this-year',
      [FilterDocumentDateFilterTypes.NextYear]:
        'context-menus.filtering.task.filters.date.presets.next-year',
      [FilterDocumentDateFilterTypes.Custom]:
        'context-menus.filtering.task.filters.date.presets.custom',
      [FilterDocumentDateFilterTypes.NoDeadline]:
        'context-menus.filtering.task.filters.date.presets.' +
        (isDeadline ? 'no-deadline' : 'no-date'),
      [FilterDocumentDateFilterTypes.HasDeadline]:
        'context-menus.filtering.task.filters.date.presets.' +
        (isDeadline ? 'has-deadline' : 'has-date'),
    };

    const groups = hideUndefinedDateOptions
      ? goalActiveFilterPresetGroups
      : dateFilterPresetGroups;

    return groups.flatMap((group, groupIndex): Option[] => {
      const presets = hideFuturePresets
        ? group.filter((preset) => !futurePresets.has(preset))
        : [...group];

      return presets.map((preset, presetIndex) => ({
        value: preset,
        label: t(labelKeys[preset]),
        hasBottomSeparator:
          groupIndex < groups.length - 1 && presetIndex === presets.length - 1,
        start: (
          <Styled.IconContainer>
            {value?.type === preset && <Icon icon={Check} />}
          </Styled.IconContainer>
        ),
      }));
    });
  }, [hideFuturePresets, hideUndefinedDateOptions, isDeadline, t, value?.type]);

  const displayText = useMemo(() => {
    if (!value) {
      return undefined;
    }

    if (value.type === FilterDocumentDateFilterTypes.Custom) {
      return formatDeadlineFilterDisplay(value, t, dateFormat, today);
    }

    return presetOptions.find((option) => option.value === value.type)?.label;
  }, [dateFormat, presetOptions, t, today, value]);

  return (
    <>
      <PopupMenuSelect
        ref={containerRef}
        label={label}
        labelSuffix={
          infoUrl ? (
            <Styled.Anchor
              href={infoUrl}
              target="_blank"
              rel="noreferrer"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              <Icon icon={Info16x16} color="inherit" />
            </Styled.Anchor>
          ) : undefined
        }
        options={presetOptions}
        onChange={handlePresetSelect}
        icon={icon}
        selectedLabel={displayText}
        placeholder={t('general.labels.select')}
        level={level}
        compact={compact}
        canClear={canClear}
        isFilterStyle
      />

      {openMenu === MenusToDisplay.Custom && (
        <PopupMenu
          referenceElement={containerRef}
          onOutside={handleCustomDateChange}
          position="bottom-end"
          hasParentWidth
        >
          <PopupMenuList hasBottomBorder>
            <PopupMenuListItem>
              <Styled.SelectContainer $compact $noHover>
                <Styled.SelectContent>
                  <Styled.SelectContentLabel>
                    {t(
                      'context-menus.filtering.task.filters.date.custom.start-label-empty',
                    )}
                  </Styled.SelectContentLabel>
                  <Styled.Select $isFilterStyle>
                    <DateButton
                      value={localStartDate}
                      onClick={openStartDatePicker}
                      dateFormat={dateFormat}
                    />
                  </Styled.Select>
                </Styled.SelectContent>
              </Styled.SelectContainer>
            </PopupMenuListItem>
            <PopupMenuListItem>
              <Styled.SelectContainer $compact $noHover>
                <Styled.SelectContent>
                  <Styled.SelectContentLabel>
                    {t(
                      'context-menus.filtering.task.filters.date.custom.end-label-empty',
                    )}
                  </Styled.SelectContentLabel>
                  <Styled.Select $isFilterStyle>
                    <DateButton
                      value={localEndDate}
                      onClick={openEndDatePicker}
                      dateFormat={dateFormat}
                    />
                  </Styled.Select>
                </Styled.SelectContent>
              </Styled.SelectContainer>
            </PopupMenuListItem>
          </PopupMenuList>
        </PopupMenu>
      )}

      {openMenu === MenusToDisplay.DatePicker && (
        <DatepickerWithOptions
          value={
            datePickerOption === DatePickerOptions.Start
              ? localStartDate ?? undefined
              : localEndDate ?? undefined
          }
          onOutside={cancelDatePicker}
          onChange={onSetCustomDate}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          referenceElement={containerRef}
        />
      )}
    </>
  );
};
